import { Backdrop, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect } from 'react';
import {
  Link as RouterLink,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { Layout, Title } from '@/features/auth';
import {
  selectIsAuthorized,
  selectIsDisabled,
  selectIsSessionLoading,
  selectIsSessionRequested,
  setupProfile,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Spinner } from '@/shared/ui';

const Link = styled(RouterLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.primary,
}));

export function AuthGuard() {
  const dispath = useAppDispatch();
  const isSessionRequested = useAppSelector(selectIsSessionRequested);
  const isSessionLoading = useAppSelector(selectIsSessionLoading);
  const isAuthorized = useAppSelector(selectIsAuthorized);
  const isDisabled = useAppSelector(selectIsDisabled);
  const location = useLocation();

  useEffect(() => {
    if (isSessionRequested) {
      return;
    }

    dispath(setupProfile());
  }, [isSessionRequested, dispath]);

  const reportRequestNewAuthLinkUsage = () => {
    Analytics.sendFeatureUsage('expired_auth_link', 'request_new_link');
  };

  if (isSessionLoading || !isSessionRequested) {
    return (
      <Backdrop open>
        <Spinner />
      </Backdrop>
    );
  } else if (!isAuthorized) {
    if (isExpiredOTP(location.hash)) {
      Analytics.sendFeatureUsage('expired_auth_link', 'view_navigated');

      return (
        <Layout>
          <Title>Link expired</Title>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
            The link you clicked has expired.
            <br /> Please{' '}
            <Link
              sx={{ color: 'primary.main' }}
              to={ROUTES.passwordReset}
              onClick={reportRequestNewAuthLinkUsage}
            >
              request a new one
            </Link>
            .
          </Typography>
        </Layout>
      );
    } else if (isDisabled) {
      return (
        <Layout>
          <Title>Account disabled</Title>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
            Please contact your administrator to activate your account.
          </Typography>
        </Layout>
      );
    }

    return <Navigate to={ROUTES.signin} />;
  }

  return <Outlet />;
}

const isExpiredOTP = (hash: string) => {
  const parsedHash = new URLSearchParams(hash.substring(1));

  return (
    parsedHash.get('error') === 'access_denied' &&
    parsedHash.get('error_code') === 'otp_expired'
  );
};
